import { useState, useEffect, useReducer, useCallback, useMemo } from 'react';
import { Auth } from '@aws-amplify/auth';
import { useApolloClient, useQuery } from '@apollo/client';
// routes
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
// config
import { AMPLIFY_API, PATH_AFTER_LOGIN } from 'src/config-global';
// company settings
import {
  fetchAndStoreCompanySettings,
  shouldRefreshCompanySettings,
  clearCompanySettings,
} from 'src/services/companySettings';
// company storage
import { clearSelectedCompany } from 'src/utils/company-storage';
// onboarding utils
import { clearOriginalPath } from 'src/utils/onboardingRedirect';
// billing API
import { validateSubscription } from 'src/api/billing';
// activity API
import { trackUserLogin } from 'src/api/activity';
// api
import { GET_CURRENT_USER } from 'src/api/queries/getCurrentUser';
import { GET_COMPANIES_FOR_USER } from 'src/api/queries/getCompaniesForUser';
import { setTourState } from 'src/components/tour/tour-store';
import { disposeDemoSession } from 'src/components/tour/demo/demo-session';
//
import { AuthContext } from './auth-context';
import { ActionMapType, AuthStateType, AuthUserType, DatabaseUserType } from '../../types';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
};

type Action = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: Action) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      user: null,
      loading: false,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

// Helper function to map user data consistently for dispatch payloads
// This function can be used to add additional fields to the user object
const mapUser = (user: any): AuthUserType => {
  if (!user) return null;

  return {
    ...user,
    role: 'admin', // For example, you can add additional fields here
  };
};

// ----------------------------------------------------------------------

Auth.configure(AMPLIFY_API);

// Amplify v5 rejects with these bare strings when nobody is signed in; that is the
// normal state on the login page, not a failure worth a console error.
const SIGNED_OUT_REJECTIONS = ['No current user', 'The user is not authenticated'];

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [userChallenge, setUserChallenge] = useState(null);
  const apolloClient = useApolloClient();
  const router = useRouter();

  // Helper function to check if user needs company selection
  const checkCompanySelection = useCallback(async () => {
    try {
      const { data } = await apolloClient.query({
        query: GET_COMPANIES_FOR_USER,
        fetchPolicy: 'network-only',
      });

      const companies = data?.getCompaniesForUser || [];
      return companies.length > 1;
    } catch (error) {
      console.error('Error checking companies:', error);
      return false;
    }
  }, [apolloClient]);

  const logout = useCallback(async () => {
    await Auth.signOut({ global: true });
    clearCompanySettings();
    // Tour progress and the demo client live in module scope, so they outlive
    // the client-side route replacement this logout ends with. Without this a
    // second login in the same tab inherits the previous user's tour position
    // and, worse, its demo Apollo client.
    setTourState(null);
    disposeDemoSession();
    // Clear Apollo cache to prevent state leakage between users
    await apolloClient.clearStore();
    // Clear company selection from localStorage
    clearSelectedCompany();
    // Clear onboarding redirect path from sessionStorage
    clearOriginalPath();
    dispatch({ type: Types.LOGOUT });
  }, [apolloClient]);

  const initialize = useCallback(async () => {
    try {
      // Run both Cognito operations in parallel for faster initialization
      // No separate /validate-token call needed because:
      // 1. currentSession() validates the JWT exists and is not expired
      // 2. Backend GraphQL middleware validates tokens on every request
      const [, user] = await Promise.all([
        Auth.currentSession(), // Validates session exists
        Auth.currentAuthenticatedUser({ bypassCache: true }), // Fetches user details
      ]);

      dispatch({
        type: Types.INITIAL,
        payload: { user: mapUser(user) },
      });

      // Fetch company settings if not already fetched recently
      const companyId = user.attributes['custom:companyId'];
      if (companyId && shouldRefreshCompanySettings()) {
        await fetchAndStoreCompanySettings(companyId, apolloClient);
      }
    } catch (error) {
      if (typeof error !== 'string' || !SIGNED_OUT_REJECTIONS.includes(error)) {
        console.error('Authentication error:', error);
      }
      await logout();
    }
  }, [apolloClient, logout]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(
    async (email: string, password: string) => {
      const currentUser = await Auth.signIn(email, password);

      if (currentUser?.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setUserChallenge(currentUser);
        return { userNeedsNewPassword: true };
      }

      if (currentUser) {
        // Always dispatch auth state first so user is considered authenticated
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: mapUser(currentUser),
          },
        });

        // Track user login activity (fire-and-forget)
        try {
          const session = await Auth.currentSession();
          const accessToken = session.getAccessToken().getJwtToken();
          trackUserLogin(accessToken).catch((error) => {
            console.debug('Failed to track user login:', error);
            // Don't block login on tracking failure
          });
        } catch (error) {
          console.debug('Failed to get token for activity tracking:', error);
          // Don't block login on tracking failure
        }

        // Check if user has multiple companies
        const needsCompanySelection = await checkCompanySelection();

        if (needsCompanySelection) {
          return { ...currentUser, needsCompanySelection: true };
        }

        // Fetch and store company settings on login
        const companyId = currentUser.attributes['custom:companyId'];
        if (companyId) {
          await fetchAndStoreCompanySettings(companyId, apolloClient);

          // Check subscription status after successful login
          try {
            const subscriptionResult = await validateSubscription(companyId);
            if (!subscriptionResult.has_subscription) {
              // Set a flag or show a message about subscription requirement
              sessionStorage.setItem(
                'subscription_required_message',
                'Your subscription has expired or is inactive. Please update your billing to continue using the application.'
              );
              router.push(paths.settings.billing);
              return { ...currentUser, subscription_inactive: true };
            }
          } catch (error) {
            console.error('Error checking subscription status:', error);
            // Continue with normal flow if subscription check fails
          }
        }
      }
      return currentUser;
    },
    [apolloClient, router, checkCompanySelection]
  );

  // COMPLETE NEW PASSWORD
  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      if (!userChallenge) {
        throw new Error('No user in challenge state');
      }
      const completedUser = await Auth.completeNewPassword(userChallenge, newPassword);

      if (completedUser?.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setUserChallenge(completedUser);
      } else {
        setUserChallenge(null);

        // Update auth state with the completed user
        try {
          // --- Fetch the full user object AFTER completing the password ---
          const fullUser = await Auth.currentAuthenticatedUser({ bypassCache: true });
          // ----------------------------------------------------------------

          const mappedUser = mapUser(fullUser); // Use the fullUser object here

          dispatch({
            type: Types.INITIAL,
            payload: {
              user: mappedUser, // Dispatch the correctly mapped user
            },
          });

          // Fetch company settings if applicable, using attributes from fullUser
          const companyId = fullUser.attributes?.['custom:companyId']; // Use fullUser here
          if (companyId) {
            // Wait for settings fetch *after* dispatching state update
            await fetchAndStoreCompanySettings(companyId, apolloClient);
          }

          // Redirect AFTER state update and settings fetch (if applicable)
          router.replace(PATH_AFTER_LOGIN);
        } catch (stateError) {
          console.error('Error updating auth state or redirecting:', stateError);
          // Optionally, trigger logout or show an error message
          await logout(); // Logout on error to ensure clean state
        }
      }

      // Return the user object, although the view no longer depends on it for redirect
      return completedUser;
    },
    [userChallenge, apolloClient, router, logout]
  );

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email: string) => {
    await Auth.forgotPassword(email);
  }, []);

  // NEW PASSWORD
  const newPassword = useCallback(async (email: string, code: string, password: string) => {
    await Auth.forgotPasswordSubmit(email, code, password);
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  // Database user query - only run when authenticated
  // Skip if not authenticated to prevent unnecessary API calls on login page
  const {
    data: currentUserData,
    loading: currentUserLoading,
    error: currentUserError,
    refetch: refetchCurrentUser,
  } = useQuery(GET_CURRENT_USER, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    skip: status !== 'authenticated', // Only run when definitively authenticated
  });

  const currentUser: DatabaseUserType = currentUserData?.getCurrentUser || null;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'amplify',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      // Database user fields
      currentUser,
      currentUserLoading,
      currentUserError,
      refetchCurrentUser,
      //
      login,
      logout,
      newPassword,
      forgotPassword,
      completeNewPassword,
    }),
    [
      status,
      state.user,
      currentUser,
      currentUserLoading,
      currentUserError,
      refetchCurrentUser,
      //
      login,
      logout,
      newPassword,
      forgotPassword,
      completeNewPassword,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
